import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/authStore'
import { isPlatformRole } from '@/types/auth'

export interface SwitchTarget {
  tenantId: string
  tenantName: string
  isPlatform: boolean
}

export interface UseSwitchTenantOptions {
  /**
   * 'dashboard' (default, the sidebar switcher): navigate to the target
   * context's home after a switch.
   * 'stay': keep the current URL and let the page re-render under the new
   * tenant (used by the "not in this organization" state, so the same deep
   * link is re-tried in the organization the user just chose).
   * A cross-role switch (platform<->tenant) always does a full load of the
   * target home: route guards check the role synchronously.
   */
  after?: 'dashboard' | 'stay'
}

/**
 * The one tenant-switch flow, extracted from PersonaSidebar (#301) so the
 * sidebar switcher and the not-in-organization state share it. The cache and
 * tenant-scoped state are reset inside authStore.switchTenant
 * (resetClientState), not here.
 */
export function useSwitchTenant({ after = 'dashboard' }: UseSwitchTenantOptions = {}) {
  const navigate = useNavigate()
  const switchTenant = useAuthStore((s) => s.switchTenant)
  const [isSwitching, setIsSwitching] = useState(false)

  const switchTo = useCallback(
    async (target: SwitchTarget): Promise<boolean> => {
      setIsSwitching(true)
      try {
        const current = useAuthStore.getState().user
        const wasPlatform = !!current && isPlatformRole(current.role)
        await switchTenant(target.tenantId)

        // Cross-role switch changes the user's role, which TenantOnly/
        // PlatformOnly route guards check synchronously. React hasn't
        // re-rendered yet, so navigate() would hit the stale guard and bounce
        // back. A full page reload reads the freshly persisted JWT.
        const targetPath = target.isPlatform ? '/platform' : '/tenant/dashboard'
        if (wasPlatform !== target.isPlatform) {
          // Zustand persist flushes asynchronously. Force a synchronous
          // localStorage write that matches the persist shape exactly, then
          // redirect after a tick so the middleware's own write lands first.
          const s = useAuthStore.getState()
          localStorage.setItem(
            'vharta-auth',
            JSON.stringify({
              state: {
                user: s.user,
                token: s.token,
                refreshToken: s.refreshToken,
                isAuthenticated: s.isAuthenticated,
                tenantMemberships: s.tenantMemberships,
                pendingTenantSelection: s.pendingTenantSelection,
              },
              version: 0,
            }),
          )
          if (s.token) localStorage.setItem('vharta-auth-token', s.token)
          setTimeout(() => window.location.assign(targetPath), 50)
          return true
        }

        toast.success(target.isPlatform ? 'Switched to Platform Admin' : `Switched to ${target.tenantName}`, {
          description: target.isPlatform
            ? 'You are now operating with platform-level access.'
            : 'All actions now affect this organization.',
          duration: 3000,
        })
        if (after === 'dashboard') navigate(targetPath)
        setIsSwitching(false)
        return true
      } catch {
        setIsSwitching(false)
        toast.error('Failed to switch context. Please try again.')
        return false
      }
    },
    [switchTenant, navigate, after],
  )

  return { switchTo, isSwitching }
}
